import type { ReactNode } from 'react'
import { useId, useState } from 'react'
import { FormattedMessage } from 'react-intl'

import { cn } from '@gto/ui/cn'
import { Field } from '@gto/ui/field'
import { IconButton } from '@gto/ui/icon-button'
import { EyeIcon, EyeSlashIcon } from '@gto/ui/icons/outline'
import type { InputProps } from '@gto/ui/input'
import { Input } from '@gto/ui/input'
import { Typography } from '@gto/ui/typography'

import type { Translations } from '@/localise'

type TextInputProps = {
  label: keyof Translations
  error?: string | null
  helper?: ReactNode
  className?: string
  input?: InputProps
}

// MUI's FormHelperText rendered at 12px; Field.Error defaults to 10px.
const ERROR_CLASSNAME = 'text-[12px]'

export const TextInput = ({ label, error, helper, className, input }: TextInputProps) => {
  const generatedId = useId()
  const id = input?.id ?? generatedId
  const [showPassword, setShowPassword] = useState(false)

  const isPassword = input?.type === 'password'
  const hasError = Boolean(error)
  const messageId = hasError || helper !== undefined ? `${id}-message` : undefined

  // Password fields own their end slot: the eye toggle replaces any caller endAddon.
  const endAddon = isPassword ? (
    <IconButton
      size={input.size === 'medium' ? 'medium' : 'small'}
      onClick={() => {
        setShowPassword((prev) => !prev)
      }}
    >
      {showPassword ? <EyeIcon /> : <EyeSlashIcon />}
    </IconButton>
  ) : (
    input?.endAddon
  )

  let message: ReactNode = null
  if (hasError) {
    message = (
      <Field.Error
        match
        className={ERROR_CLASSNAME}
        id={messageId}
        role="alert"
      >
        {error}
      </Field.Error>
    )
  } else if (helper !== undefined) {
    message = (
      <Typography
        className="px-2"
        color="text.secondary"
        component="p"
        id={messageId}
        variant="input/helper"
      >
        {helper}
      </Typography>
    )
  }

  return (
    <Field.Root
      className={cn('gap-0.5', className)}
      invalid={hasError}
    >
      {/* Size doubles as the legacy label look: medium = auth-page body1, small = account-page body2. */}
      <Field.Label
        className={input?.size === 'medium' ? 'px-0' : undefined}
        htmlFor={id}
      >
        <Typography
          color="text.secondary"
          component="span"
          variant={input?.size === 'medium' ? 'body1' : 'body2'}
        >
          <FormattedMessage id={label} />
        </Typography>
      </Field.Label>
      <Input
        aria-describedby={messageId}
        aria-invalid={hasError || undefined}
        {...input}
        endAddon={endAddon}
        id={id}
        type={isPassword && showPassword ? 'text' : input?.type}
      />
      {message}
    </Field.Root>
  )
}
